import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {PDFApi} from "components/src/api/portal/generated";
import {CopyToClipboard} from "components/src/common/CopyToClipboardButton";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {PrivacyPolicy as PdfIcon} from "iconoir-react";
import {useEffect, useRef, useState} from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import {useNavigate, useSearchParams} from "react-router-dom";
import {PDFAuthenticatorSetupWizard} from "./PDFAuthenticatorSetupWizard";

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

const sanitizeHTML = (html : string) => {
    return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
}

export const PDFGeneratePassword = () =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * searchParams is used to get the code from the URL
     */
    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded JSON containing the OTP parameters
     */
    const [otpCode, setOtpCode] = useState<string>(searchParams.get("d")??"");
    /**
     * The generated password
     */
    const [password, setPassword] = useState<string>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while generating the password
     */
    const [generating, setGenerating] = useState<boolean>(false);
    /**
     * If true, the "CipherMail PDF Authenticator" wizard will be shown
     */
    const [showSetupWizard, setShowSetupWizard] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * Reference to the OTP code input, used to set focus on page load
     */
    const otpCodeRef = useRef<HTMLInputElement>(null);

    useEffect(() =>
    {
        otpCodeRef.current?.focus();
    }, []);

    const generatePassword = () =>
    {
        logger.debug("Getting password")

        if (otpCode === undefined) {
            throw new Error("otpCode is not set")
        }

        setErrorMessage(undefined);
        setGenerating(true);

        pdfApi.pDFControllerGetPdfPassword(otpCode)
            .then((response) => {
                setPassword(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error generating password", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setGenerating(false);
            });
    }

    /**
     * Is called when the Setup Wizard is closed
     */
    const onPDFAuthenticatorSetupWizardClose = () => {
        setShowSetupWizard(false);
    }

    return (
        <div className="d-flex flex-column h-100">
            <PageHeader icon={<PdfIcon />} title={t("portal.pdf-generate-password.title")}/>

            <PageContent className="flex-grow-1 min-h-0">

                <Row>
                    <Col lg={8} md={12}>
                        <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-generate-password.intro"]))} />
                    </Col>
                </Row>
                <Row>
                    <Col lg={8} md={12}>
                        <RenderAlert message={errorMessage}/>

                        {showSetupWizard && <PDFAuthenticatorSetupWizard onClose={onPDFAuthenticatorSetupWizardClose}/>}

                        <FloatingLabel label={t('portal.pdf-generate-password.otp-label')}
                                       className="text-secondary">
                            <Form.Control ref={otpCodeRef} type="text" value={otpCode} placeholder="OTP code"
                                          onChange={e => setOtpCode(e.target.value)}/>
                        </FloatingLabel>

                        <div className="mt-4 d-flex flex-row">
                            <FloatingLabel label={t('portal.pdf-generate-password.password-label')}
                                           className="text-secondary w-100">
                                <Form.Control className="" type="text" value={password} placeholder="Password" disabled={true}
                                              readOnly={true}/>

                            </FloatingLabel>
                            <CopyToClipboard value={password}/>
                        </div>
                    </Col>
                </Row>
                <Row className="mt-4">
                    <Col lg={8} md={12}>
                        <Button
                            variant="success"
                            type="submit"
                            onClick={() => generatePassword()}
                            disabled={StringUtils.isBlank(otpCode)}
                        >
                            {generating && <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                aria-hidden="true"
                                className="me-1"
                            />}
                            {generating ? t('portal.pdf-generate-password.generating') : t('portal.pdf-generate-password.generate')}
                        </Button>
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col lg={8} md={12}>
                        <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-generate-password.pdf-authenticator-app-info"]))} />
                        <Button
                            className="mt-3"
                            variant="primary"
                            onClick={() => setShowSetupWizard(true)}
                        >
                            {t('portal.pdf-generate-password.setup-pdf-authenticator-button')}
                        </Button>
                    </Col>
                </Row>
            </PageContent>
        </div>
    )
}
